import React from 'react';
import { Calendar, Filter } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectOption,
} from '../ui';
import type { TimeRecordFilters } from '../../types/time-records.types';
import type { Employee } from '../../hooks/useEmployees';

interface Department {
  id: string;
  name: string;
}

interface Position {
  id: string;
  name: string;
}

interface PaginatedEmployees {
  data: Employee[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface PaginatedDepartments {
  data: Department[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface PaginatedPositions {
  data: Position[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface TimeRecordsFiltersProps {
  filters: TimeRecordFilters;
  onFiltersChange: (filters: Partial<TimeRecordFilters>) => void;
  employees?: PaginatedEmployees;
  departments?: PaginatedDepartments;
  positions?: PaginatedPositions;
}

export function TimeRecordsFilters({
  filters,
  onFiltersChange,
  employees,
  departments,
  positions,
}: TimeRecordsFiltersProps) {
  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ dateFrom: e.target.value || undefined, page: 1 });
  };

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ dateTo: e.target.value || undefined, page: 1 });
  };

  const handleEmployeeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFiltersChange({ employeeId: e.target.value || undefined, page: 1 });
  };

  const handleDepartmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFiltersChange({ department_id: e.target.value || undefined, page: 1 });
  };

  const handlePositionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFiltersChange({ position_id: e.target.value || undefined, page: 1 });
  };

  const handleLateFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onFiltersChange({
      is_late: value === 'late' ? true : value === 'not_late' ? false : undefined,
      page: 1,
    });
  };

  const handleEarlyLeaveFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onFiltersChange({
      left_early: value === 'early' ? true : value === 'not_early' ? false : undefined,
      page: 1,
    });
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFiltersChange({ sort: e.target.value || undefined });
  };

  // Get default dates (last 30 days)
  const getDefaultDateFrom = () => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  };

  const getDefaultDateTo = () => {
    return new Date().toISOString().split('T')[0];
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-lg">Фильтры и поиск</CardTitle>
        </div>
        <CardDescription>
          Используйте фильтры для поиска записей учёта времени
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {/* Date From */}
          <div className="relative">
            <Calendar className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              type="date"
              placeholder="Дата от"
              value={filters.dateFrom || getDefaultDateFrom()}
              onChange={handleDateFromChange}
              className="pl-10"
            />
          </div>

          {/* Date To */}
          <div className="relative">
            <Calendar className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              type="date"
              placeholder="Дата до"
              value={filters.dateTo || getDefaultDateTo()}
              onChange={handleDateToChange}
              className="pl-10"
            />
          </div>

          {/* Employee filter */}
          <Select
            placeholder="Сотрудник"
            value={filters.employeeId || ''}
            onChange={handleEmployeeChange}
          >
            <SelectOption value="">Все сотрудники</SelectOption>
            {employees?.data?.map((emp) => (
              <SelectOption key={emp.id} value={emp.id}>
                {emp.last_name} {emp.first_name}
              </SelectOption>
            ))}
          </Select>

          {/* Department filter */}
          <Select
            placeholder="Подразделение"
            value={filters.department_id || ''}
            onChange={handleDepartmentChange}
          >
            <SelectOption value="">Все подразделения</SelectOption>
            {departments?.data?.map((dept) => (
              <SelectOption key={dept.id} value={dept.id}>
                {dept.name}
              </SelectOption>
            ))}
          </Select>

          {/* Position filter */}
          <Select
            placeholder="Должность"
            value={filters.position_id || ''}
            onChange={handlePositionChange}
          >
            <SelectOption value="">Все должности</SelectOption>
            {positions?.data?.map((pos) => (
              <SelectOption key={pos.id} value={pos.id}>
                {pos.name}
              </SelectOption>
            ))}
          </Select>

          {/* Late filter */}
          <Select
            placeholder="Опоздания"
            value={
              filters.is_late === true
                ? 'late'
                : filters.is_late === false
                  ? 'not_late'
                  : ''
            }
            onChange={handleLateFilterChange}
          >
            <SelectOption value="">Все</SelectOption>
            <SelectOption value="late">Опоздали</SelectOption>
            <SelectOption value="not_late">Без опозданий</SelectOption>
          </Select>

          {/* Early leave filter */}
          <Select
            placeholder="Ранние уходы"
            value={
              filters.left_early === true
                ? 'early'
                : filters.left_early === false
                  ? 'not_early'
                  : ''
            }
            onChange={handleEarlyLeaveFilterChange}
          >
            <SelectOption value="">Все</SelectOption>
            <SelectOption value="early">Ушли рано</SelectOption>
            <SelectOption value="not_early">Без ранних уходов</SelectOption>
          </Select>

          {/* Sort */}
          <Select value={filters.sort || 'work_date:DESC'} onChange={handleSortChange}>
            <SelectOption value="work_date:DESC">Сначала новые</SelectOption>
            <SelectOption value="work_date:ASC">Сначала старые</SelectOption>
            <SelectOption value="worked_seconds:DESC">Больше отработано</SelectOption>
            <SelectOption value="worked_seconds:ASC">Меньше отработано</SelectOption>
          </Select>
        </div>
      </CardContent>
    </Card>
  );
}
